/**
 * Find and highlight text in a DOM container.
 * Used for citation highlighting in document previews (DOCX, HTML, TXT, PDF).
 * Supports exact and fuzzy matching with fallback strategies.
 */

import { logger } from "@/lib/logger"

import { findTextWithFuzzyMatching, type MatchQuality } from "./fuzzy-text-matcher"
import { normalizeContainerForMapping, normalizeForMatching } from "./normalize-for-matching"

const HIGHLIGHT_CLASS = "citation-highlight"
const FUZZY_HIGHLIGHT_CLASS = "citation-highlight-fuzzy"
const PARTIAL_HIGHLIGHT_CLASS = "citation-highlight-partial"
const WEAK_HIGHLIGHT_CLASS = "citation-highlight-weak"

function getHighlightModifierClass(quality: MatchQuality): string {
  switch (quality) {
    case "exact":
      return ""
    case "fuzzy":
      return FUZZY_HIGHLIGHT_CLASS
    case "partial":
      return PARTIAL_HIGHLIGHT_CLASS
    case "weak":
      return WEAK_HIGHLIGHT_CLASS
    default:
      return ""
  }
}

export type HighlightResult = {
  found: boolean
  quality: MatchQuality
  confidence: number
}

/**
 * Find and highlight text in the container using exact or fuzzy matching.
 * Returns the match quality and confidence.
 */
export function highlightTextInContainer(
  container: HTMLElement,
  searchSnippet: string
): HighlightResult {
  if (!searchSnippet || !searchSnippet.trim()) {
    return { found: false, quality: "none", confidence: 0 }
  }

  const normalizedSearch = normalizeForMatching(searchSnippet)
  if (!normalizedSearch) {
    return { found: false, quality: "none", confidence: 0 }
  }

  const textNodes: Text[] = []
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => {
      const text = node.textContent?.trim()
      if (!text) return NodeFilter.FILTER_REJECT
      return NodeFilter.FILTER_ACCEPT
    },
  })

  let node: Text | null
  while ((node = walker.nextNode() as Text | null)) {
    textNodes.push(node)
  }

  const fullText = textNodes.map((n) => n.textContent || "").join("")
  const normalizedFull = normalizeContainerForMapping(fullText)

  // Match on one stable normalized stream to keep DOM index mapping consistent.
  const matchResult = findTextWithFuzzyMatching(normalizedFull, normalizedSearch, {
    alreadyNormalized: true,
  })

  if (!matchResult.found) {
    return { found: false, quality: "none", confidence: 0 }
  }

  const searchIndex = matchResult.startIndex
  const searchEnd = matchResult.endIndex
  let currentIndex = 0
  let highlighted = false

  for (const textNode of textNodes) {
    const text = textNode.textContent || ""
    const normalizedText = normalizeContainerForMapping(text, { trimEdges: false })
    const nodeLength = normalizedText.length
    const nodeStart = currentIndex
    const nodeEnd = currentIndex + nodeLength

    if (nodeEnd <= searchIndex || nodeStart >= searchEnd) {
      currentIndex = nodeEnd
      continue
    }

    const overlapStart = Math.max(0, searchIndex - nodeStart)
    const overlapEnd = Math.min(nodeLength, searchEnd - nodeStart)

    const parent = textNode.parentNode
    if (!parent) continue

    const origStart = mapNormalizedToOriginal(text, overlapStart)
    const origEnd = mapNormalizedToOriginal(text, overlapEnd)
    const beforeText = text.substring(0, origStart)
    const matchText = text.substring(origStart, origEnd)
    const afterText = text.substring(origEnd)

    if (!matchText) {
      logger.debug("citation highlight: WARNING - matchText is empty, skipping")
      currentIndex = nodeEnd
      continue
    }

    const fragment = document.createDocumentFragment()
    if (beforeText) fragment.appendChild(document.createTextNode(beforeText))
    const mark = document.createElement("mark")
    const modifierClass = getHighlightModifierClass(matchResult.quality)
    mark.className = modifierClass ? `${HIGHLIGHT_CLASS} ${modifierClass}` : HIGHLIGHT_CLASS
    mark.setAttribute("data-confidence", matchResult.confidence.toFixed(2))
    // react-pdf text layer uses color:transparent on spans.
    // Base styles for exact; fuzzy/partial/weak get their styles from CSS classes.
    mark.style.borderRadius = "2px"
    if (matchResult.quality === "exact") {
      mark.style.backgroundColor = "rgba(147, 51, 234, 0.3)"
    } else if (matchResult.quality === "fuzzy") {
      mark.style.backgroundColor = "rgba(234, 179, 8, 0.4)"
      mark.style.borderBottom = "2px dashed rgba(234, 179, 8, 0.8)"
    }
    mark.appendChild(document.createTextNode(matchText))
    fragment.appendChild(mark)
    if (afterText) fragment.appendChild(document.createTextNode(afterText))

    parent.replaceChild(fragment, textNode)
    highlighted = true
    currentIndex = nodeEnd
    // Continue to next node in case match spans multiple nodes
  }

  if (!highlighted) {
    logger.debug("citation highlight: match found but failed to create DOM element", {
      searchIndex,
      searchEnd,
      textNodesCount: textNodes.length,
      normalizedFullLength: normalizedFull.length,
    })
  }

  return { found: highlighted, quality: matchResult.quality, confidence: matchResult.confidence }
}

/**
 * Map a position in normalized text back to original text.
 * Builds normalized string incrementally to find the original offset.
 */
function mapNormalizedToOriginal(original: string, normalizedIndex: number): number {
  const normalized = normalizeContainerForMapping(original, { trimEdges: false })
  if (normalizedIndex >= normalized.length) return original.length
  let origIdx = 0
  let normIdx = 0
  let lastWasSpace = false
  for (; origIdx < original.length; origIdx++) {
    if (normIdx >= normalizedIndex) break
    const char = original[origIdx]
    if (/\s/.test(char)) {
      if (!lastWasSpace) {
        normIdx++
        lastWasSpace = true
      }
    } else {
      normIdx++
      lastWasSpace = false
    }
  }
  return origIdx
}

/**
 * Remove all citation highlights from the container.
 */
export function clearHighlights(container: HTMLElement): void {
  const marks = container.querySelectorAll(`mark.${HIGHLIGHT_CLASS}`)
  marks.forEach((mark) => {
    const parent = mark.parentNode
    if (parent) {
      parent.replaceChild(document.createTextNode(mark.textContent || ""), mark)
      parent.normalize()
    }
  })
}

type ScrollHighlightOptions = {
  preferredContainer?: HTMLElement | null
  maxPasses?: number
  verticalBiasPx?: number
}

function isVerticallyScrollable(element: HTMLElement): boolean {
  const styles = window.getComputedStyle(element)
  const overflowY = styles.overflowY
  const allowsScroll = overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay"
  return allowsScroll && element.scrollHeight > element.clientHeight + 1
}

function getNearestScrollableAncestor(element: HTMLElement): HTMLElement | null {
  let current: HTMLElement | null = element.parentElement
  while (current) {
    if (isVerticallyScrollable(current)) {
      return current
    }
    current = current.parentElement
  }
  return null
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

function getOffsetTopWithinContainer(mark: HTMLElement, container: HTMLElement): number | null {
  let current: HTMLElement | null = mark
  let top = 0
  while (current && current !== container) {
    top += current.offsetTop
    current = current.offsetParent as HTMLElement | null
  }
  return current === container ? top : null
}

function scrollHighlightIntoContainer(
  container: HTMLElement,
  mark: HTMLElement,
  options: { verticalBiasPx?: number } = {}
): void {
  const { verticalBiasPx = 0 } = options
  const relativeTop = getOffsetTopWithinContainer(mark, container)
  // Fallback only if offsetParent chain does not reach the container.
  const markTop =
    relativeTop ??
    mark.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop
  const targetScrollTop =
    markTop - container.clientHeight / 2 + mark.offsetHeight / 2 + verticalBiasPx
  const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight)
  container.scrollTop = clamp(targetScrollTop, 0, maxScrollTop)
}

/**
 * Scroll citation highlight into view in a deterministic way.
 * Uses one explicit scroll container instead of relying on browser
 * ancestor heuristics from Element.scrollIntoView.
 */
export function scrollCitationHighlightIntoView(
  mark: HTMLElement,
  options: ScrollHighlightOptions = {}
): () => void {
  const { preferredContainer = null, maxPasses = 2, verticalBiasPx = 0 } = options
  const container =
    preferredContainer && isVerticallyScrollable(preferredContainer)
      ? preferredContainer
      : getNearestScrollableAncestor(mark)
  if (!container) {
    return () => {}
  }

  const frameIds: number[] = []
  const passes = Math.max(1, maxPasses)
  const runPass = (pass: number) => {
    scrollHighlightIntoContainer(container, mark, { verticalBiasPx })
    if (pass >= passes) {
      return
    }
    const frameId = requestAnimationFrame(() => runPass(pass + 1))
    frameIds.push(frameId)
  }

  const firstFrame = requestAnimationFrame(() => runPass(1))
  frameIds.push(firstFrame)

  return () => {
    frameIds.forEach((frameId) => cancelAnimationFrame(frameId))
  }
}
